import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Hammer, Check, X, RotateCcw, Pencil, Loader2, Plus, Search, Megaphone, Lightbulb, Undo2 } from 'lucide-react';
import { buildHintsService, BuildHint, BuildHintStatus } from '../../services/buildHintsService';

const POLL_MS = 20_000;
const TOAST_MS = 8_000;
type Filter = BuildHintStatus | 'all';
type Sort = 'priority' | 'newest';
type Patch = { change?: string; status?: BuildHintStatus; note?: string };

const STATUS_DOT: Record<BuildHintStatus, string> = { open: 'bg-amber-400', done: 'bg-emerald-500', dismissed: 'bg-slate-300' };
const STATUS_LABEL: Record<BuildHintStatus, string> = { open: 'Open', done: 'Done', dismissed: 'Dismissed' };
const SOURCE_LABEL: Record<string, string> = { agentic: 'Heard by the platform', manual: 'Added here', mcp: 'Added over MCP' };
const SOURCE_PILL: Record<string, string> = { manual: 'bg-slate-100 text-slate-600', mcp: 'bg-indigo-50 text-indigo-600' };

const GROUPS: Array<{ key: string; label: string; match: (h: BuildHint) => boolean }> = [
  { key: 'asked', label: 'Asked for in the room', match: (h) => h.trigger === 'asked' },
  { key: 'relevant', label: 'Inferred from the discussion', match: (h) => h.trigger === 'relevant' },
  { key: 'other', label: 'Added by hand or over MCP', match: (h) => h.trigger !== 'asked' && h.trigger !== 'relevant' },
];

const byNewest = (a: BuildHint, b: BuildHint) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id);

const ago = (iso: string) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' });
};
const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const ranges = (seq: number[]) => {
  const s = [...new Set(seq)].sort((a, b) => a - b);
  const out: string[] = [];
  for (let i = 0; i < s.length; ) {
    let j = i;
    while (j + 1 < s.length && s[j + 1] === s[j] + 1) j++;
    out.push(j > i ? `${s[i]}–${s[j]}` : `${s[i]}`);
    i = j + 1;
  }
  return out.join(', ');
};

const btn = 'inline-flex items-center justify-center gap-1.5 text-sm px-4 h-9 rounded-xl font-bold transition-colors disabled:opacity-50';
const btnGhost = `${btn} border border-slate-200 bg-white hover:bg-slate-50 text-slate-600`;

const TriggerIcon: React.FC<{ trigger?: string; size?: number }> = ({ trigger, size = 13 }) =>
  trigger === 'asked' ? <Megaphone size={size} className="text-amber-600" aria-label="Asked for in the room" />
    : trigger === 'relevant' ? <Lightbulb size={size} className="text-sky-600" aria-label="Inferred from the discussion" />
      : null;

interface Props {
  sessionId: string;
  canEdit: boolean;
}

/** Prototype changes for the facilitator's build loop: heard by the platform, added here or over MCP. */
export const BuildHintsTab: React.FC<Props> = ({ sessionId, canEdit }) => {
  const [hints, setHints] = useState<BuildHint[]>([]);
  const [counts, setCounts] = useState<Record<BuildHintStatus, number>>({ open: 0, done: 0, dismissed: 0 });
  const [filter, setFilter] = useState<Filter>('open');
  const [sort, setSort] = useState<Sort>('priority');
  const [source, setSource] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newChange, setNewChange] = useState('');
  const [adding, setAdding] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ ids: string[]; label: string } | null>(null);
  const cursor = useRef<string | null>(null);
  const known = useRef<Set<string> | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addRef = useRef<HTMLInputElement>(null);

  const merge = useCallback((changed: BuildHint[]) => {
    setHints((prev) => {
      const byId = new Map(prev.map((h) => [h.id, h]));
      for (const h of changed) byId.set(h.id, h);
      return [...byId.values()];
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await buildHintsService.list(sessionId, cursor.current ?? undefined);
      if (known.current) {
        const added = res.hints.filter((h) => !known.current!.has(h.id) && h.status === 'open').map((h) => h.id);
        if (added.length) setFresh((prev) => new Set([...prev, ...added]));
      } else {
        known.current = new Set();
      }
      res.hints.forEach((h) => known.current!.add(h.id));
      merge(res.hints);
      setCounts(res.counts);
      cursor.current = res.cursor;
      setError(null);
    } catch {
      setError('Could not load build hints.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, merge]);

  useEffect(() => {
    cursor.current = null;
    known.current = null;
    setHints([]);
    setFresh(new Set());
    setChecked(new Set());
    setSelectedId(null);
    setLoading(true);
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return hints
      .filter((h) => filter === 'all' || h.status === filter)
      .filter((h) => source === 'all' || h.source === source)
      .filter((h) => !q || [h.change, h.note ?? '', ...h.quotes].some((t) => t.toLowerCase().includes(q)))
      .sort(byNewest);
  }, [hints, filter, source, query]);

  const sections = useMemo(() => {
    if (sort === 'newest') return [{ key: 'all', label: '', items: shown }];
    return GROUPS.map((g) => ({ key: g.key, label: g.label, items: shown.filter(g.match) })).filter((g) => g.items.length > 0);
  }, [shown, sort]);

  const ordered = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const selected = ordered.find((h) => h.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected && ordered.length) setSelectedId(ordered[0].id);
    if (!ordered.length && selectedId) setSelectedId(null);
  }, [selected, ordered, selectedId]);

  useEffect(() => {
    setEditing(false);
    setNote('');
    setDraft(selected?.change ?? '');
    if (selected && fresh.has(selected.id)) setFresh((prev) => { const n = new Set(prev); n.delete(selected.id); return n; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const showToast = (ids: string[], label: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ ids, label });
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  };

  const patchOne = async (id: string, patch: Patch) => {
    merge([await buildHintsService.update(sessionId, id, patch)]);
  };

  const setStatus = async (ids: string[], status: BuildHintStatus, withNote?: string) => {
    if (!ids.length) return;
    setBusy(true);
    const idx = ordered.findIndex((h) => h.id === selectedId);
    const next = ordered.slice(idx + 1).concat(ordered.slice(0, idx).reverse()).find((h) => !ids.includes(h.id));
    try {
      await Promise.all(ids.map((id) => patchOne(id, { status, ...(withNote?.trim() ? { note: withNote.trim() } : {}) })));
      setChecked(new Set());
      setNote('');
      if (ids.includes(selectedId ?? '') && filter !== 'all') setSelectedId(next?.id ?? null);
      if (status !== 'open') showToast(ids, `${ids.length > 1 ? `${ids.length} hints` : 'Hint'} ${status === 'done' ? 'marked done' : 'dismissed'}`);
      await load();
    } catch {
      setError('Could not update the hint.');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!toast) return;
    const ids = toast.ids;
    setToast(null);
    await setStatus(ids, 'open');
  };

  const saveEdit = async () => {
    if (!selected || !draft.trim()) return;
    setBusy(true);
    try {
      await patchOne(selected.id, { change: draft.trim() });
      setEditing(false);
      await load();
    } catch {
      setError('Could not update the hint.');
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!newChange.trim()) return;
    setAdding(true);
    try {
      const created = await buildHintsService.create(sessionId, newChange.trim());
      known.current?.add(created.id);
      merge([created]);
      setNewChange('');
      setFilter('open');
      setSource('all');
      setQuery('');
      setSelectedId(created.id);
      await load();
    } catch {
      setError('Could not add the hint.');
    } finally {
      setAdding(false);
    }
  };

  const toggleChecked = (id: string) => setChecked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allChecked = ordered.length > 0 && ordered.every((h) => checked.has(h.id));
  const checkedOpen = [...checked].filter((id) => hints.find((h) => h.id === id)?.status === 'open');

  const move = (delta: number) => {
    if (!ordered.length) return;
    const idx = ordered.findIndex((h) => h.id === selectedId);
    setSelectedId(ordered[Math.min(ordered.length - 1, Math.max(0, idx + delta))].id);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const t = e.target as HTMLElement;
    if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === '/') { e.preventDefault(); document.getElementById('build-hints-search')?.focus(); }
    else if (e.key === 'n' && canEdit) { e.preventDefault(); addRef.current?.focus(); }
    else if (canEdit && selected) {
      if (e.key === 'x' && selected.status !== 'dismissed') { e.preventDefault(); setChecked((p) => { const n = new Set(p); n.delete(selected.id); return n; }); setStatus(checked.size > 1 && checked.has(selected.id) ? [...checked] : [selected.id], 'dismissed'); }
      else if (e.key === 'd' && selected.status !== 'done') { e.preventDefault(); setStatus(checked.size > 1 && checked.has(selected.id) ? [...checked] : [selected.id], 'done'); }
      else if (e.key === 'e') { e.preventDefault(); setEditing(true); }
      else if (e.key === ' ') { e.preventDefault(); toggleChecked(selected.id); }
    }
  };

  const filters: Array<[Filter, string]> = [['open', `Open ${counts.open}`], ['done', `Done ${counts.done}`], ['dismissed', `Dismissed ${counts.dismissed}`], ['all', 'All']];
  const freshCount = [...fresh].filter((id) => ordered.some((h) => h.id === id) && id !== selectedId).length;

  const renderRow = (h: BuildHint) => {
    const active = h.id === selectedId;
    return (
      <div
        key={h.id}
        data-testid="build-hint"
        onClick={() => setSelectedId(h.id)}
        className={`group flex gap-3 px-4 py-3 border-b border-slate-100 cursor-pointer transition-colors ${active ? 'bg-primary/5 border-l-2 border-l-primary' : fresh.has(h.id) ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-slate-50'} ${h.status === 'dismissed' ? 'opacity-60' : ''}`}
      >
        {canEdit && (
          <input
            type="checkbox"
            className="mt-1 shrink-0 accent-[#00c7c4]"
            checked={checked.has(h.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleChecked(h.id)}
            aria-label={`Select ${h.change}`}
          />
        )}
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold text-navy leading-snug line-clamp-2 ${h.status === 'done' ? 'line-through decoration-emerald-500' : ''}`}>{h.change}</p>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
            <TriggerIcon trigger={h.trigger} />
            {SOURCE_PILL[h.source] && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${SOURCE_PILL[h.source]}`}>{h.source}</span>}
            {!!h.repeats && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">×{h.repeats + 1}</span>}
            {filter === 'all' && <span className={`w-2 h-2 rounded-full ${STATUS_DOT[h.status]}`} title={STATUS_LABEL[h.status]} />}
            {fresh.has(h.id) && <span className="text-[10px] font-bold text-amber-700">NEW</span>}
            <span className="ml-auto" title={clock(h.createdAt)}>{ago(h.createdAt)}</span>
          </div>
        </div>
        {canEdit && h.status === 'open' && (
          <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button className="p-1.5 rounded-lg text-emerald-700 hover:bg-emerald-50" title="Done (d)" aria-label="Done" onClick={(e) => { e.stopPropagation(); setStatus([h.id], 'done'); }}><Check size={14} /></button>
            <button className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Dismiss (x)" aria-label="Dismiss" onClick={(e) => { e.stopPropagation(); setStatus([h.id], 'dismissed'); }}><X size={14} /></button>
          </div>
        )}
      </div>
    );
  };

  const detail = selected && (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <span className={`w-2 h-2 rounded-full ${STATUS_DOT[selected.status]}`} />
          <span className="text-slate-600">{STATUS_LABEL[selected.status]}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500">{SOURCE_LABEL[selected.source] ?? selected.source}</span>
          {selected.trigger && (
            <>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1 text-slate-500"><TriggerIcon trigger={selected.trigger} />{selected.trigger === 'asked' ? 'Asked for in the room' : 'Inferred from the discussion'}</span>
            </>
          )}
          {!!selected.repeats && <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">raised ×{selected.repeats + 1}</span>}
        </div>

        {editing ? (
          <div className="flex flex-col gap-2">
            <textarea
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-base font-semibold text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
              rows={4}
              value={draft}
              maxLength={240}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Edit change"
              autoFocus
            />
            <div className="flex items-center gap-2">
              <button className={`${btn} bg-primary hover:bg-primary-dark text-white shadow-sm`} disabled={busy || !draft.trim()} onClick={saveEdit}>Save</button>
              <button className={btnGhost} onClick={() => { setEditing(false); setDraft(selected.change); }}>Cancel</button>
              <span className="ml-auto text-xs text-slate-400">{draft.length}/240</span>
            </div>
          </div>
        ) : (
          <h2 className={`text-lg font-bold text-navy leading-snug ${selected.status === 'done' ? 'line-through decoration-emerald-500' : ''}`}>{selected.change}</h2>
        )}

        {selected.quotes.length > 0 && (
          <section>
            <h3 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-2">What was said</h3>
            <div className="flex flex-col gap-2">
              {selected.quotes.map((q, i) => (
                <blockquote key={i} className="text-sm text-slate-600 italic border-l-2 border-primary/40 pl-3">“{q}”</blockquote>
              ))}
            </div>
          </section>
        )}

        {selected.reasoning && (
          <section>
            <h3 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-2">Why the platform suggested it</h3>
            <p className="text-sm text-slate-600">{selected.reasoning}</p>
          </section>
        )}

        {selected.note && (
          <section>
            <h3 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-2">{selected.status === 'dismissed' ? 'Why not' : 'What changed'}</h3>
            <p className="text-sm text-slate-600">{selected.note}</p>
          </section>
        )}

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs border-t border-slate-100 pt-4">
          <div><dt className="text-slate-400">Raised</dt><dd className="text-slate-600 font-medium">{new Date(selected.createdAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</dd></div>
          <div><dt className="text-slate-400">Last updated</dt><dd className="text-slate-600 font-medium">{ago(selected.updatedAt)}{selected.updatedBy ? ` by ${selected.updatedBy}` : ''}</dd></div>
          {selected.evidenceSeq.length > 0 && <div><dt className="text-slate-400">Transcript lines</dt><dd className="text-slate-600 font-medium">{ranges(selected.evidenceSeq)}</dd></div>}
          {selected.source === 'agentic' && <div><dt className="text-slate-400">Confidence</dt><dd className="text-slate-600 font-medium">{Math.round(selected.confidence * 100)}%</dd></div>}
        </dl>
      </div>

      {canEdit && !editing && (
        <div className="border-t border-[#e2e8f0] bg-slate-50/50 px-6 py-4 flex flex-col gap-3">
          {selected.status === 'open' ? (
            <>
              <input
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 bg-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                placeholder="Note (optional): what changed, or why not"
                value={note}
                maxLength={500}
                onChange={(e) => setNote(e.target.value)}
                aria-label="Note"
              />
              <div className="flex gap-2">
                <button className={`${btn} border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700`} disabled={busy} onClick={() => setStatus([selected.id], 'done', note)}><Check size={14} /> Done</button>
                <button className={btnGhost} disabled={busy} onClick={() => setStatus([selected.id], 'dismissed', note)}><X size={14} /> Dismiss</button>
                <button className={`${btn} text-slate-500 hover:text-navy`} onClick={() => setEditing(true)}><Pencil size={14} /> Edit</button>
              </div>
            </>
          ) : (
            <div className="flex gap-2">
              <button className={btnGhost} disabled={busy} onClick={() => setStatus([selected.id], 'open')}><RotateCcw size={14} /> Reopen</button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display" onKeyDown={onKeyDown}>
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Hammer size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Build hints</h1>
              <p className="text-slate-500 text-sm font-medium">
                {counts.open} open, {counts.done} done: prototype changes for the build loop, heard in the room, added here or over MCP
              </p>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2 w-[460px] max-w-[50%]">
              <input
                ref={addRef}
                className="flex-1 min-w-0 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                placeholder="Add a change (n)"
                value={newChange}
                maxLength={240}
                onChange={(e) => setNewChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                aria-label="New build hint"
              />
              <button
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-40"
                disabled={adding || !newChange.trim()}
                onClick={add}
              >
                {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add
              </button>
            </div>
          )}
        </header>

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0] flex flex-wrap gap-3 items-center">
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden" role="tablist">
              {filters.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={filter === id}
                  onClick={() => setFilter(id)}
                  className={`px-3 py-2 text-xs font-semibold transition-all ${filter === id ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                id="build-hints-search"
                className="w-full border border-[#e2e8f0] rounded-xl pl-9 pr-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                placeholder="Search changes, quotes and notes (/)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search build hints"
              />
            </div>
            <select className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 bg-white outline-none focus:ring-2 focus:ring-primary" value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
              <option value="all">All sources</option>
              <option value="agentic">Heard by the platform</option>
              <option value="manual">Added here</option>
              <option value="mcp">Added over MCP</option>
            </select>
            <select className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs font-semibold text-slate-600 bg-white outline-none focus:ring-2 focus:ring-primary" value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Sort">
              <option value="priority">Asked first</option>
              <option value="newest">Newest first</option>
            </select>
          </div>

          {error && <p className="px-5 py-3 border-b border-[#e2e8f0] bg-red-50 text-sm font-medium text-red-700">{error}</p>}

          {loading ? (
            <div className="flex justify-center items-center py-20 text-[#64748b]"><Loader2 className="animate-spin" /></div>
          ) : ordered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Hammer size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">{query || source !== 'all' ? 'No hints match these filters.' : filter === 'open' ? 'No open build hints.' : 'Nothing here yet.'}</p>
              <p className="text-slate-400 text-xs mt-2">Hints appear when the room asks for a prototype change, with the transcript drain and agentic intents on.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(360px,440px)_1fr] h-[calc(100vh-300px)] min-h-[440px]">
              <div className="flex flex-col min-h-0 border-b lg:border-b-0 lg:border-r border-[#e2e8f0]">
                {canEdit && (
                  <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50/50 text-xs text-slate-500 min-h-[41px]">
                    <input
                      type="checkbox"
                      className="accent-[#00c7c4]"
                      checked={allChecked}
                      onChange={() => setChecked(allChecked ? new Set() : new Set(ordered.map((h) => h.id)))}
                      aria-label="Select all shown"
                    />
                    {checked.size === 0 ? (
                      <span>{ordered.length} shown · j/k move, d done, x dismiss, e edit</span>
                    ) : (
                      <>
                        <span className="font-semibold text-navy">{checked.size} selected</span>
                        {checkedOpen.length > 0 && (
                          <>
                            <button className="font-bold text-emerald-700 hover:underline disabled:opacity-50" disabled={busy} onClick={() => setStatus(checkedOpen, 'done')}>Done</button>
                            <button className="font-bold text-slate-600 hover:underline disabled:opacity-50" disabled={busy} onClick={() => setStatus(checkedOpen, 'dismissed')}>Dismiss</button>
                          </>
                        )}
                        <button className="ml-auto hover:underline" onClick={() => setChecked(new Set())}>Clear</button>
                      </>
                    )}
                  </div>
                )}
                {freshCount > 0 && (
                  <button
                    className="px-4 py-2 text-xs font-bold text-amber-800 bg-amber-50 border-b border-amber-100 hover:bg-amber-100 text-left"
                    onClick={() => { const first = ordered.find((h) => fresh.has(h.id)); if (first) setSelectedId(first.id); }}
                  >
                    {freshCount} new {freshCount === 1 ? 'hint' : 'hints'} from the room
                  </button>
                )}
                <div className="flex-1 overflow-y-auto" role="list">
                  {sections.map((s) => (
                    <div key={s.key}>
                      {s.label && (
                        <div className="sticky top-0 z-10 px-4 py-1.5 bg-white/95 backdrop-blur border-b border-slate-100 text-[11px] font-bold text-slate-400 tracking-widest uppercase">
                          {s.label} · {s.items.length}
                        </div>
                      )}
                      {s.items.map(renderRow)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="min-h-0" data-testid="build-hint-detail">{detail}</div>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-navy text-white text-sm font-medium rounded-xl px-4 py-3 shadow-lg" role="status">
          {toast.label}
          <button className="inline-flex items-center gap-1 font-bold text-primary hover:underline" onClick={undo}><Undo2 size={14} /> Undo</button>
        </div>
      )}
    </div>
  );
};

export default BuildHintsTab;
